import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useExamLayoutPattern } from 'hooks/useFeatureFlag';
import { chain } from 'lodash';
import { appServiceExamV3ObjectTypes } from 'slices/appService.slice';
import { fieldsSelector } from 'slices/field.slice';
import { selectObjectTypesMap } from 'slices/objectTypes.slice';
import {
  ELayoutPattern,
  ESeatingMethod,
  ESeatingMode,
} from 'types/examV3Settings.type';
import { errorNotification } from 'utils/notifications';
import { EApplicationObjectTypeGroup } from '@timeedit/types';
import { TEInput } from '@timeedit/ui-components';
import { Checkbox, Space } from '@timeedit/ui-components/antd';
import type { CheckboxChangeEvent } from '@timeedit/ui-components/antd/checkbox/Checkbox';
import type { DefaultOptionType } from '@timeedit/ui-components/antd/select/index';
import {
  fetchAllocationMode,
  seatAllocationSettingsSelector,
  updateSeatAllocateSetting,
} from '../../../../slices/examV3Settings.slice';
import { useAppDispatch, useAppSelector } from '../../../../slices/store';
import './SeatAllocationSettings.scss';

const SeatAllocationSettings = (props: { isLoading: boolean }) => {
  const dispatch = useAppDispatch();
  const sentErrorMessage = useRef(false);
  const seatAllocationSettings = useAppSelector(seatAllocationSettingsSelector);

  useEffect(() => {
    dispatch(fetchAllocationMode());
  }, [dispatch]);

  const handleChange = useCallback(
    (key, value) => {
      dispatch(
        updateSeatAllocateSetting({
          [key]: {
            ...seatAllocationSettings[key],
            ...value,
          },
        })
      );
    },
    [dispatch, seatAllocationSettings]
  );

  const examV3Mapping = useAppSelector(appServiceExamV3ObjectTypes);
  const allFields = useAppSelector(fieldsSelector);
  const objectMapping = useAppSelector(selectObjectTypesMap);

  const studentExamMapping = examV3Mapping.find(
    (mapping) =>
      mapping.applicationObjectTypeGroup === EApplicationObjectTypeGroup.STUDENT
  );

  useEffect(() => {
    if (
      !!examV3Mapping?.length &&
      !studentExamMapping &&
      !sentErrorMessage.current &&
      !props.isLoading
    ) {
      errorNotification('Student mapping missing', [
        'Sorting by student field might not work unless the Student object is mapped in the Object type section to the left',
      ]);
      sentErrorMessage.current = true;
    }
  }, [examV3Mapping, studentExamMapping, props.isLoading]);

  const studentObjectMapping = objectMapping[studentExamMapping?.objectTypeId];

  const studentFieldIds = useMemo(
    () =>
      studentObjectMapping?.fields.concat(studentObjectMapping.parentFields),
    [studentObjectMapping]
  );

  const fieldOptions = useMemo(() => {
    const fields = chain(allFields)
      .filter((field) => studentFieldIds?.includes(field.id))
      .map((field) => ({ label: field.extId, value: field.id }))
      .value();
    return [...fields, { label: 'Object id', value: null }];
  }, [allFields, studentFieldIds]);

  const showLayoutPattern = useExamLayoutPattern();

  return (
    <Space size="large" direction="vertical" style={{ padding: '20px' }}>
      <Space size="small" direction="horizontal">
        <TEInput.Select
          label="Seating modes"
          value={seatAllocationSettings.seatingMode.mode}
          className="seat-allocation-settings-select"
          onChange={(e) => handleChange('seatingMode', { mode: e })}
          allowClear={false}
          options={[
            { value: ESeatingMode.StudentSpecific, label: 'Student Specific' },
            { value: ESeatingMode.StudentRange, label: 'Student Range' },
            { value: ESeatingMode.ExamRange, label: 'Exam Range' },
          ]}
        />
        <Checkbox
          checked={seatAllocationSettings.seatingMode.allowOverride}
          onChange={(e: CheckboxChangeEvent) =>
            handleChange('seatingMode', { allowOverride: e.target.checked })
          }
        >
          Allow override
        </Checkbox>
      </Space>
      <Space size="small" direction="horizontal">
        <TEInput.Select
          label="Seating method"
          value={seatAllocationSettings.seatingMethod.mode}
          className="seat-allocation-settings-select"
          onChange={(e) => handleChange('seatingMethod', { mode: e })}
          allowClear={false}
          options={[
            { value: ESeatingMethod.clumped, label: 'Clumped' },
            { value: ESeatingMethod.spread, label: 'Spread' },
          ]}
        />
        <Checkbox
          checked={seatAllocationSettings.seatingMethod.allowOverride}
          onChange={(e: CheckboxChangeEvent) =>
            handleChange('seatingMethod', { allowOverride: e.target.checked })
          }
        >
          Allow override
        </Checkbox>
      </Space>
      {showLayoutPattern && (
        <Space size="small" direction="horizontal">
          <TEInput.Select
            label="Layout pattern"
            value={
              seatAllocationSettings.layoutPattern?.mode ??
              ELayoutPattern.standard
            }
            className="seat-allocation-settings-select"
            onChange={(e) => handleChange('layoutPattern', { mode: e })}
            allowClear={false}
            options={[
              { value: ELayoutPattern.standard, label: 'Standard' },
              { value: ELayoutPattern.snake, label: 'Snake' },
            ]}
          />
          <Checkbox
            checked={
              seatAllocationSettings.layoutPattern?.allowOverride ?? false
            }
            onChange={(e: CheckboxChangeEvent) =>
              handleChange('layoutPattern', { allowOverride: e.target.checked })
            }
          >
            Allow override
          </Checkbox>
        </Space>
      )}
      <Space size="small" direction="horizontal">
        <Space size="small" direction="vertical" align="end">
          <TEInput.Select
            label="Sort by student field"
            className="seat-allocation-settings-select"
            value={
              !fieldOptions.length
                ? seatAllocationSettings.sorting?.sortByName
                : seatAllocationSettings.sorting?.sortBy
            }
            defaultValue={null}
            allowClear={false}
            options={fieldOptions}
            onChange={(newVal, record: DefaultOptionType) => {
              return handleChange('sorting', {
                sortBy: newVal ?? null,
                sortByName: record?.label ?? null,
              });
            }}
          />
          <TEInput.Select
            className="seat-allocation-settings-select"
            allowClear={false}
            value={seatAllocationSettings.sorting?.sort ?? 'asc'}
            onChange={(newVal) => handleChange('sorting', { sort: newVal })}
            options={sortOptions}
          />
        </Space>
        <Checkbox
          checked={seatAllocationSettings.sorting?.allowOverride}
          onChange={(e: CheckboxChangeEvent) =>
            handleChange('sorting', { allowOverride: e.target.checked })
          }
        >
          Allow override
        </Checkbox>
      </Space>
    </Space>
  );
};

const sortOptions = [
  {
    label: 'Ascending',
    value: 'asc',
  },
  {
    label: 'Descending',
    value: 'desc',
  },
];

export default SeatAllocationSettings;
